import React from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Input,
  Select,
  Toggle,
  ColumnLayout,
  Slider,
  Grid,
  SelectProps,
} from '@cloudscape-design/components';
import RequiredFieldLabel from '../../RequiredFieldLabel';
import { QuestionFormParams } from '../QuestionFormWizard';
import { Question } from 'src/types/helios/Question';
import { Division } from 'src/types/orbit/Division';
import { Category } from 'src/types/helios/Category';
import { Location } from 'src/types/helios/Location';
import Tags from './Tags';
import { convertItem } from 'src/utils/generalUtils';
import { AUDIT_SCORES_OPTIONS, AuditTooltipMessages } from 'src/contstant/commonLabels';
import SingleSelectDivision from '../../division/SingleSelectDivision';
import QuestionCategoryDropdown from '../../questionCategory/QuestionCategoryDropdown';
import Tooltip from '../../Tooltip';

interface BasicInformationProps {
  formData: QuestionFormParams;
  setFormData: (
    data: QuestionFormParams | ((prev: QuestionFormParams) => QuestionFormParams),
  ) => void;
  fieldValidationError: Record<string, string>;
  divisionList: Division[];
  categories: Category[];
  question?: Question;
  isCopyQuestion?: boolean;
  tagsList?: any[];
  locationsList?: Location[];
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  formData,
  setFormData,
  fieldValidationError,
  divisionList,
  categories,
  question,
  isCopyQuestion,
  tagsList = [],
  locationsList = [],
}) => {
  return (
    <Container>
      <SpaceBetween size="l">
        <FormField
          data-testid="overview-field"
          label={<RequiredFieldLabel label="Overview" />}
          info={<Tooltip content={AuditTooltipMessages.QUESTION_OVERVIEW} />}
          errorText={fieldValidationError.title}
        >
          <Input
            data-testid="overview-input"
            value={formData.title}
            onChange={({ detail }) =>
              setFormData((prev) => ({
                ...prev,
                title: detail.value,
              }))
            }
          />
        </FormField>

        <FormField
          data-testid="business-unit-field"
          label={<RequiredFieldLabel label="Business Unit" />}
          errorText={fieldValidationError.businessUnit}
        >
          <Input
            data-testid="business-unit-input"
            readOnly={true}
            value={formData.businessUnit}
            onChange={({ detail }) =>
              setFormData((prev) => ({
                ...prev,
                businessUnit: detail.value,
              }))
            }
          />
        </FormField>

        <FormField
          data-testid="division-field"
          label={<RequiredFieldLabel label="Division" />}
          info={<Tooltip content={AuditTooltipMessages.DIVISION} />}
          errorText={fieldValidationError.divisionName}
        >
          <SingleSelectDivision
            data-testid="division-select"
            divisions={divisionList}
            isLoading={false}
            error={null}
            readOnly={question && isCopyQuestion !== true}
            selectedDivision={formData.divisionName}
            onItemSelection={(item: Division | null) => {
              setFormData((prevState) => ({
                ...prevState,
                divisionName: item?.divisionName || '',
              }));
            }}
          />
        </FormField>

        <FormField
          data-testid="category-field"
          label={'Category'}
          info={<Tooltip content={AuditTooltipMessages.CATEGORY} />}
        >
          <QuestionCategoryDropdown
            data-testid="category-dropdown"
            categories={categories}
            selectedCategory={formData.categories}
            onChange={(selectedCategories) =>
              setFormData((prev) => ({
                ...prev,
                categories: selectedCategories || [],
              }))
            }
          />
        </FormField>

        <FormField
          data-testid="audit-score-field"
          label={<RequiredFieldLabel label="Audit Score" />}
          description="Audit score determines question's importance and weight in the audit"
          info={<Tooltip content={AuditTooltipMessages.AUDIT_SCORE} />}
          errorText={fieldValidationError.auditScore}
        >
          <Grid
            data-testid="audit-score-grid"
            gridDefinition={[{ colspan: { default: 10 } }, { colspan: { default: 2 } }]}
          >
            <Slider
              data-testid="audit-score-slider"
              onChange={({ detail }) =>
                setFormData((prev) => ({
                  ...prev,
                  auditScore: detail.value.toString(),
                }))
              }
              value={Number(formData.auditScore || 1)}
              max={20}
              min={1}
            />

            <Select
              data-testid="audit-score-select"
              filteringType="auto"
              selectedOption={convertItem<string, SelectProps.Option>(
                formData.auditScore || '1',
                (item) => ({
                  value: item,
                  label: item,
                }),
              )}
              onChange={({ detail }) =>
                setFormData((prev) => ({
                  ...prev,
                  auditScore: detail.selectedOption.value!,
                }))
              }
              options={AUDIT_SCORES_OPTIONS}
            />
          </Grid>
        </FormField>

        {/* Reuse Tags component instead of duplicating code */}
        <Tags
          formData={formData}
          setFormData={setFormData}
          fieldValidationError={fieldValidationError}
          tagsList={tagsList}
          locationsList={locationsList}
        />

        <ColumnLayout data-testid="fail-requirements-column" columns={2}>
          <FormField
            data-testid="require-comment-on-fail-field"
            label="Comment Required on Fail"
            info={
              <Tooltip content="When enabled, auditors must provide a comment when marking this question as Fail." />
            }
          >
            <Toggle
              data-testid="require-comment-on-fail-toggle"
              checked={formData.requireCommentOnFail ?? false}
              onChange={({ detail }) =>
                setFormData((prev) => ({
                  ...prev,
                  requireCommentOnFail: detail.checked,
                }))
              }
            />
          </FormField>
          <FormField
            data-testid="require-evidence-on-fail-field"
            label="Attachment Required on Fail"
            info={
              <Tooltip content="When enabled, auditors must attach evidence when marking this question as Fail." />
            }
          >
            <Toggle
              data-testid="require-evidence-on-fail-toggle"
              checked={formData.requireEvidenceOnFail ?? false}
              onChange={({ detail }) =>
                setFormData((prev) => ({
                  ...prev,
                  requireEvidenceOnFail: detail.checked,
                }))
              }
            />
          </FormField>
        </ColumnLayout>

        <ColumnLayout data-testid="pass-requirements-column" columns={2}>
          <FormField
            data-testid="require-comment-on-pass-field"
            label="Comment Required on Pass"
            info={
              <Tooltip content="When enabled, auditors must provide a comment when marking this question as Pass." />
            }
          >
            <Toggle
              data-testid="require-comment-on-pass-toggle"
              checked={formData.requireCommentOnPass ?? false}
              onChange={({ detail }) =>
                setFormData((prev) => ({
                  ...prev,
                  requireCommentOnPass: detail.checked,
                }))
              }
            />
          </FormField>
          <FormField
            data-testid="require-evidence-on-pass-field"
            label="Attachment Required on Pass"
            info={
              <Tooltip content="When enabled, auditors must attach evidence when marking this question as Pass." />
            }
          >
            <Toggle
              data-testid="require-evidence-on-pass-toggle"
              checked={formData.requireEvidenceOnPass ?? false}
              onChange={({ detail }) =>
                setFormData((prev) => ({
                  ...prev,
                  requireEvidenceOnPass: detail.checked,
                }))
              }
            />
          </FormField>
        </ColumnLayout>

        <ColumnLayout data-testid="activation-column" columns={1}>
          <FormField
            data-testid="activation-field"
            label="Activation Status"
            stretch
            description="Enable or disable the question using this switch."
          >
            <Toggle
              data-testid="activation-toggle"
              checked={formData.isActive}
              onChange={({ detail }) =>
                setFormData((prev) => ({ ...prev, isActive: detail.checked }))
              }
            />
          </FormField>
        </ColumnLayout>
      </SpaceBetween>
    </Container>
  );
};

export default BasicInformation;
